import React from 'react';

const tableClasses = [
  // Hook class is `catalog-table`, NOT `table`: `table` is a Tailwind display
  // utility (display:table), which would override the div's display and break it
  // as a flex scroll container. (Selectors that targeted `.table` are updated.)
  'catalog-table',
  // flex-1 min-h-0: as the (flex-column) catalog's growable child, this is the
  // scroll container — fills the space between catalog-tools and catalog-stats
  // and scrolls internally (sticky <th>), so the page-body never scrolls.
  'relative box-border min-h-0 w-full flex-1 overflow-auto',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:text-left',
  // Header band: uppercase micro-label, not sentence-case body text. The panel
  // wrapper owns the corner radius now, so the old rounded-l/r-card on the first
  // and last th are gone (they clipped against the panel edge).
  '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:box-border [&_thead_th]:cursor-pointer [&_thead_th]:bg-page-alt [&_thead_th]:px-3 [&_thead_th]:py-2.5 [&_thead_th]:text-[11px] [&_thead_th]:font-semibold [&_thead_th]:tracking-caps [&_thead_th]:uppercase [&_thead_th]:text-fg-3',
  '[&_thead_th.row-select]:z-[2] [&_thead_th.row-select]:w-0 [&_thead_th.row-select]:border-t-0 [&_thead_th.row-select]:bg-page-alt [&_thead_th.row-select]:px-3 [&_thead_th.row-select]:py-0 [&_thead_th.row-select]:text-center',
  '[&_thead_th.row-actions]:z-[2] [&_thead_th.row-actions]:bg-page-alt',
  '[&_thead_th.sort-asc]:after:absolute [&_thead_th.sort-asc]:after:top-2 [&_thead_th.sort-asc]:after:text-[0.65rem] [&_thead_th.sort-asc]:after:opacity-80 [&_thead_th.sort-asc]:after:content-[\'▲\']',
  '[&_thead_th.sort-desc]:after:absolute [&_thead_th.sort-desc]:after:top-2 [&_thead_th.sort-desc]:after:text-[0.65rem] [&_thead_th.sort-desc]:after:opacity-80 [&_thead_th.sort-desc]:after:content-[\'▼\']',
  '[&_tbody_tr]:border-b [&_tbody_tr]:border-soft [&_tbody_tr:last-child]:border-0',
  '[&_tbody_tr:hover]:bg-page-alt',
  '[&_tbody_td]:box-border [&_tbody_td]:px-3 [&_tbody_td]:py-2.5 [&_tbody_td]:text-[13px] [&_tbody_td]:text-fg-1',
  // Opt-in for the numbers-in-mono rule: a Column can set className="mono".
  '[&_td.mono]:font-mono [&_td.mono]:tabular-nums',
  // The full-load state is a skeleton now, so the old rules are gone: the
  // opacity-80 washed out placeholders that are already deliberately low
  // contrast, and .loading-flex (the centred spinner, min-h-40) only ever
  // matched DataTable's single colSpan cell, which collapsed the table height.
  // Skeleton rows are not interactive, so they must not answer to :hover.
  '[&_tbody_tr.skeleton-row:hover]:bg-transparent',
  '[&_tbody.soft-loading]:opacity-80',
  '[&_table_.row-select]:sticky [&_table_.row-select]:left-0 [&_table_.row-select]:z-[1] [&_table_.row-select]:w-0 [&_table_.row-select]:bg-panel [&_table_.row-select]:px-3 [&_table_.row-select]:py-0 [&_table_.row-select]:text-center',
  '[&_table_.row-select_input[type=checkbox]]:align-middle',
  '[&_table_.row-actions]:sticky [&_table_.row-actions]:right-0 [&_table_.row-actions]:z-[1] [&_table_.row-actions]:w-20 [&_table_.row-actions]:px-3 [&_table_.row-actions]:py-0 [&_table_.row-actions]:text-center',
  '[&_table_.row-actions_a]:mr-1 [&_table_.row-actions_a]:cursor-pointer',
  '[&_table_.row-actions_a:last-child]:mr-0',
  '[&_table_.row-actions_a_svg]:w-3',
  // Row-action icons follow the cell's text color (Icon defaults to
  // currentColor), so they need only a muted text color, not a stroke override.
  '[&_table_.row-actions]:text-fg-3',
  '[&_table_.row-actions_a:hover]:text-primary',
].join(' ');

interface TableProps {
  id?: string;
  children: React.ReactNode;
  onScrollBottom?(): void | Promise<void>;
}

let handlingScrollBottom = false;

export const Table = (props: TableProps) => {
  const handleScroll = async (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target = e.target as HTMLElement;
    const hitBottom = target.scrollHeight - target.scrollTop - 0.1 * target.scrollHeight <= target.clientHeight;
    if (hitBottom && !handlingScrollBottom) {
      handlingScrollBottom = true;
      await props.onScrollBottom?.();
      handlingScrollBottom = false;
    }
  };
  return (
    <div id={props.id} className={tableClasses} onScroll={handleScroll}>
      {props.children}
    </div>
  );
};
